type AcmService = 'mcp';

declare global {
  interface Window {
    __ACM_SERVICE_URLS__?: Partial<Record<AcmService, string>>;
  }
}

function configuredUrl(service: AcmService): string | undefined {
  const runtime = window.__ACM_SERVICE_URLS__?.[service];
  const build = import.meta.env[`VITE_ACM_${service.toUpperCase()}_URL`] as string | undefined;
  return runtime || build;
}

/**
 * Browser is a presentation client, but its control traffic still enters the
 * real, separately addressed MCP proxy. Authentication is the login
 * session; identity resolution and ACL stay in the proxy.
 *
 * Published acm-self URLs use the deterministic pair
 *   <hash>.acm.<base>     -> UI
 *   <hash>.acm-mcp.<base> -> MCP
 * Codable hub aliases use
 *   codable-<hash>.<base>     -> UI
 *   codable-<hash>-mcp.<base> -> MCP
 * Direct node access keeps the hostname and changes :9080 -> :9082.
 * A custom publication must inject window.__ACM_SERVICE_URLS__.mcp or set the
 * VITE_ACM_MCP_URL build value.
 */
export function mcpServiceBaseUrl(): string {
  const configured = configuredUrl('mcp');
  if (configured) return configured.replace(/\/+$/, '');

  const url = new URL(window.location.href);
  url.protocol = 'https:';
  url.pathname = '';
  url.search = '';
  url.hash = '';

  const labels = url.hostname.split('.');
  if (labels.length >= 3 && labels[1] === 'acm') {
    labels[1] = 'acm-mcp';
    url.hostname = labels.join('.');
    url.port = '';
  } else if (labels.length >= 3 && /^codable-[a-z0-9-]+$/i.test(labels[0] ?? '')) {
    labels[0] = `${labels[0]}-mcp`;
    url.hostname = labels.join('.');
    url.port = '';
  } else {
    url.port = '9082';
  }
  return url.toString().replace(/\/+$/, '');
}

export function mcpServiceUrl(path = '/mcp'): string {
  return `${mcpServiceBaseUrl()}${path.startsWith('/') ? path : `/${path}`}`;
}

/** Legacy binary/stream bridge; still enters through the separately addressed MCP proxy. */
export function apiProxyUrl(path: string): string {
  const normalized = path.startsWith('/') ? path : `/${path}`;
  return mcpServiceUrl(`/proxy/local${normalized}`);
}
